import { selectReplyChannel, rememberReplyChannel } from '../../lib/replyChannelSelection';
import DeliveryReview from './DeliveryReview';
import { onOpenTemplateSettingsRequest } from '../../lib/templateSettingsReturn';
import { useIsCompanyAdmin } from './useScheduledMessages';
// BL-240 Gate 2 — staff-initiated reply composer for the intake session detail panel.
//
// App chat uses the caller-JWT adapter below. For transport channels, the send itself is the already-live `staff-send-reply` Edge Function
// (verify_jwt: true), reached through supabase-js so the signed-in staff member's JWT rides
// along. Nothing here writes to intake_sessions directly — the function owns the outbound send
// and the log append; this panel only shows what was typed until the next poll returns it.

import AppChatReplyComposer from './AppChatReplyComposer';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { supabaseClient } from '../../config/supabase';
import {
    bodyLimitForChannel,
    buildOptimisticOutboundEntry,
    describeChannel,
    resolveReplyRecipient,
    sendStaffReply,
    STAFF_REPLY_PICKER_CHANNELS,
    validateStaffReply,
} from '../../lib/staffReply';
import {
    contactHasWhatsApp,
    contactReachOptions,
    isWhatsAppGeneralAllowed,
} from '../../lib/communicationsCompose';
import {
    fillTemplateBody,
    buildTemplateVariableValues,
    getTemplateVariables,
} from '../../lib/communicationsTemplates';
import MessageComposerBox, { firstNameOnly } from './MessageComposerBox';
import { smsOptOutMessage } from '../../lib/smsConsent';
import { UNCONFIRMED_SEND_MESSAGE } from '../../lib/communicationsThread';
import { threadBusinessNumber, replyBlockedReason } from '../../lib/communicationRecipients';
import { newClientRef } from '../../lib/optimisticMessages';
import { formatPhone } from '../../lib/phone';
import { pickDefaultSendingIdentity, resolveThreadSendingNumber } from '../../lib/smsSenderSelection';
import {
    appendSignature,
    assembleSignature,
} from '../../lib/signatures';
import { signatureChipFor, useSignatureContext } from './useMessageSignature';
import { valuesForDeliveryTime } from '../../lib/templateFieldResolver';
import { scheduleStaffReply } from '../../lib/scheduledSendApi';
import { formatScheduledWhen, recipientTimeZoneForPhone } from '../../lib/scheduledSend';
import { resolveContactTimeZone } from '../../lib/contactTimeZone';
import { smsCapability, whatsappCapability } from '../../lib/channelCapability';
import {
    appendSignatureHtml,
    emailAddressListError,
    emailHtmlHasFormatting,
    emailHtmlToText,
    extraEmailRecipients,
    plainTextToEmailHtml,
    sanitizeEmailHtml,
} from '../../lib/emailHtml';

const OUTBOUND_MEDIA_BUCKET = 'inbound-message-media';

function TransportStaffReplyComposer({
    intakeSession,
    mailboxActions = null,
    contact = null,
    onSent,
    onEngagementStart,
    engagementState = null,
    initialBody = '',
    initialSubject = '',
    theme = 'light',
    client = supabaseClient,
    currentUserId = null,
    platformDoorContext = null,
    headerBrandEntityId = '',
    smsOptedOut = false,
    sendingIdentities = [],
    templates = [],
    selectedTemplateSid = '',
    templateVariables = {},
    onTemplateSelect,
    onTemplatePanelRequestOpen,
    // Comms next pass: told just before the reply box leaves for Settings → Templates (the host remembers the draft too).
    onLeaveForTemplateSettings,
    onSelectedIdentityChange,
    allSessions = [],
    lastSubject = '',
    // 1.12.212-beta: phone_channel_capabilities row for the client's number (line type, WhatsApp presence), or null.
    phoneCapability = null,
    companyName = '',
    // Comms gate fixes: the brand this conversation belongs to, for a template's "Company name".
    templateCompanyName = '',
    // BL-177 Step 2d: Step 3 turns this on; until then the Send menu shows "Send now" only.
    scheduleSendEnabled = false,
    onScheduleSend,
    // BL-177 Step 3: called after a message was scheduled from this composer (the thread reloads its scheduled bubbles).
    onScheduled,
}) {
    const [body, setBody] = useState('');
    const [selectedChannel, setSelectedChannel] = useState('');
    const [autoChannelReason, setAutoChannelReason] = useState('');
    const [subject, setSubject] = useState('');
    // Keep this reply mounted while the matching template tab opens in the side panel.
    const openTemplateSettings = (channel, templateId = '') => {
        onTemplatePanelRequestOpen?.('edit', channel, templateId);
    };
    const canEditTemplates = useIsCompanyAdmin(client);
    // The side panel's "Edit wording in Template Builder" / "Manage templates" links come here too, so the draft is kept.
    const openTemplateSettingsRef = useRef(openTemplateSettings);
    openTemplateSettingsRef.current = openTemplateSettings;
    useEffect(() => onOpenTemplateSettingsRequest((detail) => openTemplateSettingsRef.current(detail.channel, detail.templateId)), []);
    const [sending, setSending] = useState(false);
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');
    const [selectedIdentityId, setSelectedIdentityId] = useState('');
    const [mediaUrl, setMediaUrl] = useState('');
    const [attachmentError, setAttachmentError] = useState('');
    const [scheduling, setScheduling] = useState(false);
    const [scheduleError, setScheduleError] = useState('');
    // 1.12.212-beta (Email): Cc / Bcc and the formatted body. `body` keeps the plain-text twin so every existing check still works.
    const [emailCc, setEmailCc] = useState('');
    const [emailBcc, setEmailBcc] = useState('');
    const [emailHtml, setEmailHtml] = useState('');
    // Switching from a formatted email to SMS / WhatsApp waits for a yes: the formatting would be lost.
    const [pendingChannel, setPendingChannel] = useState('');
    const engagementStartedRef = useRef(false);
    // Set synchronously the moment a send starts, so a double Enter/click can never start two sends
    // before React has re-rendered the disabled state.
    const sendInFlightRef = useRef(false);
    const composeActionRef = useRef(null);
    const sessionId = intakeSession?.id;
    useEffect(() => {
        engagementStartedRef.current = false;
        setBody(typeof initialBody === 'string' ? initialBody : '');
        setSelectedIdentityId('');
        const previousSubject = lastSubject || initialSubject || '';
        setSubject(previousSubject
            ? (/^re:/i.test(previousSubject) ? previousSubject : `Re: ${previousSubject}`)
            : 'Re: ');
        setSelectedChannel('');
        setError('');
        setNotice('');
        setMediaUrl('');
        setAttachmentError('');
        setEmailCc('');
        setEmailBcc('');
        setEmailHtml('');
        setPendingChannel('');
    }, [sessionId, initialBody, initialSubject, lastSubject]);

    const { address } = useMemo(
        () => resolveReplyRecipient(intakeSession, selectedChannel),
        [intakeSession, selectedChannel]
    );
    const limit = bodyLimitForChannel(selectedChannel);
    const isEmail = selectedChannel === 'email';
    const lineSms = smsCapability(phoneCapability);
    const lineWhatsApp = whatsappCapability(phoneCapability);
    const ccError = isEmail ? emailAddressListError(emailCc, 'Cc') : '';
    const bccError = isEmail ? emailAddressListError(emailBcc, 'Bcc') : '';
    const copyError = ccError || bccError;
    const canCompose = Boolean(intakeSession?.id);
    const smsNotice = lineSms.state === 'blocked'
        ? lineSms.reason
        : lineSms.state === 'warn' ? `${lineSms.reason} Sending still works.` : '';
    const whatsappNotice = lineWhatsApp.state === 'not_on' ? 'This number is not on WhatsApp.' : '';
    const channelNotice = autoChannelReason || (selectedChannel === 'email' && !address
        ? 'This contact has no email address for the selected channel.'
        : canCompose && !address
            ? 'This conversation has no number or address to reply to.'
            : selectedChannel === 'sms'
                ? smsNotice
                : selectedChannel === 'whatsapp'
                    ? whatsappNotice
                    : !selectedChannel ? smsNotice || whatsappNotice : '');
    const canReply = !engagementState || engagementState.canReply !== false;
    const trimmedLength = body.trim().length;
    const overLimit = trimmedLength > limit;
    const channelIdentities = sendingIdentities.filter((identity) => identity.channel === selectedChannel);
    const threadSendingNumber = ['sms', 'mms'].includes(selectedChannel)
        ? resolveThreadSendingNumber(
            intakeSession?.external_thread_id,
            threadBusinessNumber(intakeSession),
            intakeSession?.raw_message_log,
            sendingIdentities.filter((line) => line.channel === 'sms')
        )
        : '';
    const contactPhones = Array.isArray(contact?.phones) ? contact.phones : [];
    const phoneAddress = contactReachOptions(contact, { sessions: allSessions }).find((option) => option.channel === 'sms')?.address
        || (!contactPhones.length ? intakeSession?.contact_phone : '')
        || '';
    const whatsappContact = contact || { phones: [intakeSession?.contact_phone].filter(Boolean) };
    const matchingSessions = allSessions.length > 0 ? allSessions : [intakeSession];
    const availableContactChannels = [
        ...(phoneAddress ? ['sms'] : []),
        ...(phoneAddress && contactHasWhatsApp({ contact: whatsappContact, sessions: matchingSessions })
            ? ['whatsapp'] : []),
        ...((contact?.emails?.some(Boolean) || intakeSession?.contact_email) ? ['email'] : []),
    ];
    const channelOptions = STAFF_REPLY_PICKER_CHANNELS.filter((option) => (
        availableContactChannels.includes(option)
        && sendingIdentities.some((identity) => identity.channel === option)
    ));
    const inboundKey = JSON.stringify(intakeSession?.raw_message_log || []);
    useEffect(() => {
        const selection = selectReplyChannel({ session: intakeSession, available: channelOptions, userId: currentUserId || '',
            smsBlocked: smsOptedOut ? 'SMS is opted out.' : lineSms.state === 'blocked' ? lineSms.reason : '',
            whatsappOpen: isWhatsAppGeneralAllowed({ contact: whatsappContact, sessions: matchingSessions }),
        });
        setSelectedChannel(selection.channel);
        setAutoChannelReason(selection.reason);
        // Availability and inbound activity are the only automatic selection triggers.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sessionId, inboundKey, channelOptions.join('|'), smsOptedOut, lineSms.state]);
    const sourceMailboxIdentity = selectedChannel === 'email'
        ? channelIdentities.find((identity) => identity.sourceId === intakeSession?.email_provider_id)
        : null;
    const selectedIdentity = channelIdentities.find((identity) => identity.id === selectedIdentityId && !identity.blockedReason)
        || sourceMailboxIdentity
        || pickDefaultSendingIdentity(channelIdentities, selectedChannel, threadSendingNumber)
        || null;
    const selectedTemplate = templates.find((template) => template.sid === selectedTemplateSid) || null;
    // Comms gate fixes: the WhatsApp line this conversation would send from, so WhatsApp → Templates lists that account's templates.
    const whatsappIdentities = sendingIdentities.filter((identity) => identity.channel === 'whatsapp' && !identity.blockedReason);
    const whatsappTemplateIdentity = selectedChannel === 'whatsapp'
        ? selectedIdentity
        : pickDefaultSendingIdentity(whatsappIdentities, 'whatsapp', threadSendingNumber);
    const whatsappTemplateAccountSid = whatsappTemplateIdentity ? (whatsappTemplateIdentity.twilioAccountSid || null) : undefined;
    const templateScopeMismatch = Boolean(
        selectedTemplateSid
        && selectedChannel === 'whatsapp'
        && selectedTemplate
        && (selectedTemplate.twilioAccountSid || null) !== (selectedIdentity?.twilioAccountSid || null)
    );
    const withinWhatsAppWindow = isWhatsAppGeneralAllowed({
        contact: whatsappContact,
        sessions: matchingSessions,
    });
    const whatsappTemplateRequired = selectedChannel === 'whatsapp' && !withinWhatsAppWindow;
    const smsBlocked = (selectedChannel === 'sms' || selectedChannel === 'mms') && smsOptedOut;
    const bodyToSend = selectedTemplate
        ? fillTemplateBody(selectedTemplate.body || selectedTemplate.types?.['twilio/text']?.body, templateVariables)
        : body;
    const templateIncomplete = Boolean(
        selectedTemplate
        && Object.keys(getTemplateVariables(selectedTemplate)).some((key) => !templateVariables[key]?.trim())
    );

    // BL-177 Step 2d (section 10): append the configured signature to each eligible staff message.
    const orgId = intakeSession?.org_id || '';
    const signatureContext = useSignatureContext(
        client,
        orgId,
        currentUserId,
        { platformDoorContext, headerBrandEntityId }
    );
    const signatureTimeZone = signatureContext?.timeZone || '';
    const [removeMine, setRemoveMine] = useState(false);
    useEffect(() => { setRemoveMine(false); }, [sessionId, currentUserId, signatureTimeZone]);
    // BL-177 Step 2d (section 6): the in-app pop-up's Reply button opens the conversation, then fires `ia:reply`.
    // When it names this thread, put the cursor in the reply box (now, and once more after the thread has rendered).
    const formRef = useRef(null);
    useEffect(() => {
        if (typeof window === 'undefined' || !sessionId) return undefined;
        const timers = [];
        const focusReply = () => {
            const textarea = formRef.current?.querySelector('textarea');
            if (textarea && !textarea.disabled) textarea.focus();
        };
        const onReply = (event) => {
            if (String(event?.detail?.sessionId || '') !== String(sessionId)) return;
            focusReply();
            timers.push(setTimeout(focusReply, 0));
        };
        window.addEventListener('ia:reply', onReply);
        return () => {
            window.removeEventListener('ia:reply', onReply);
            timers.forEach(clearTimeout);
        };
    }, [sessionId]);
    const signatureChannel = selectedChannel === 'mms' ? 'sms' : selectedChannel;
    const signature = signatureContext
        ? assembleSignature({
            channel: signatureChannel,
            isTemplate: Boolean(selectedTemplateSid),
            isAutomatic: false,
            companySettings: signatureContext.companySettings,
            personalSignature: signatureContext.personalSignature,
            removeMine,
        })
        : null;
    const signatureApplies = Boolean(signature?.applies);
    // Scheduled times are in the company time zone; the recipient's clock comes from their number.
    const scheduledTimeZone = signatureTimeZone || 'America/New_York';
    // the contact's saved time zone first (CRM), else the zone of their phone number
    const recipientZone = resolveContactTimeZone(contact) || (selectedChannel === 'email' ? null : recipientTimeZoneForPhone(address));

    useEffect(() => {
        onSelectedIdentityChange?.(selectedIdentity, selectedChannel);
    }, [onSelectedIdentityChange, selectedChannel, selectedIdentity]);

    const handleSend = async (event) => {
        event.preventDefault();
        setError('');
        setNotice('');
        if (!selectedChannel) {
            setError('Select a message type before sending.');
            return;
        }
        if (sendInFlightRef.current) return;
        if (copyError) {
            setError(copyError);
            return;
        }
        const validation = validateStaffReply({
            session: intakeSession,
            body: bodyToSend,
            channel: selectedChannel,
            contentSid: selectedTemplateSid,
            mediaUrl,
        });
        if (!validation.valid) {
            setError(validation.error);
            return;
        }
        if (!canReply) {
            setError('Another staff member is engaged in this conversation. Request to join before replying.');
            return;
        }
        if (smsBlocked) {
            setError(smsOptOutMessage(contact?.sms_opted_out_at));
            return;
        }
        if (signatureContext.loading || signatureContext.error) {
            setError(signatureContext.error || 'Signature settings are still loading. Please try again.');
            return;
        }
        if (whatsappTemplateRequired && !selectedTemplateSid) {
            setError('Choose an approved WhatsApp template to message outside the 24-hour window.');
            return;
        }
        if (selectedTemplateSid && !selectedTemplate) {
            setError('Choose an approved template before sending.');
            return;
        }
        if (templateScopeMismatch) {
            setError('Choose a template that belongs to the selected sending number account.');
            return;
        }
        if (selectedTemplateSid && templateIncomplete) {
            setError('Fill in every required template field before sending.');
            return;
        }
        sendInFlightRef.current = true;
        setSending(true);
        try {
            const deliveryChannel = selectedChannel === 'sms' && mediaUrl.trim() ? 'mms' : selectedChannel;
            const contentSid = selectedChannel === 'whatsapp'
                ? (selectedTemplate?.contentSid || selectedTemplateSid)
                : '';
            // BL-177 Step 2d (live bug): the send's client_ref is chosen here, so the optimistic bubble and the saved row
            // share it and the thread shows the text once — whichever reaches the screen first.
            const finalBody = signatureApplies && !selectedTemplateSid
                ? appendSignature(bodyToSend, signature)
                : bodyToSend;
            // Email: the formatted version (signature appended as plain lines), sanitized; the text part is finalBody.
            const finalHtml = isEmail && emailHtmlHasFormatting(emailHtml)
                ? (signatureApplies ? appendSignatureHtml(emailHtml, signature) : sanitizeEmailHtml(emailHtml))
                : '';
            const fingerprint=JSON.stringify([intakeSession?.id,deliveryChannel,finalBody,mediaUrl,contentSid,templateVariables,selectedIdentity?.sourceId,isEmail?[emailCc,emailBcc,finalHtml]:null]);
            if(composeActionRef.current?.fingerprint!==fingerprint)composeActionRef.current={fingerprint,key:newClientRef()};
            const clientRef=composeActionRef.current.key;
            const sendResult=await sendStaffReply({
                clientRef,
                session: intakeSession,
                body: finalBody,
                channel: deliveryChannel,
                subject: selectedChannel === 'email' ? subject : '',
                mediaUrl: mediaUrl.trim() || null,
                contentSid,
                contentVariables: contentSid ? templateVariables : null,
                templateBody: contentSid ? (selectedTemplate?.body || selectedTemplate?.types?.['twilio/text']?.body || '') : '',
                sendingIdentityId: selectedIdentity?.sourceId || null,
                sendingIdentityExplicitlyChosen: Boolean(selectedIdentityId),
                ...(isEmail ? { cc: emailCc, bcc: emailBcc, html: finalHtml } : {}),
                client,
            });
            if(!sendResult?.pending)composeActionRef.current=null;
            setRemoveMine(false);
            const entry = buildOptimisticOutboundEntry({
                body: finalBody,
                channel: deliveryChannel,
                staffUserId: currentUserId,
                mediaUrl: mediaUrl.trim() || null,
                contentSid,
                contentVariables: contentSid ? templateVariables : null,
                clientRef,
            });
            if (isEmail) {
                const copies = extraEmailRecipients({ to: address, cc: emailCc, bcc: emailBcc });
                Object.assign(entry, {
                    ...(subject.trim() ? { subject: subject.trim() } : {}),
                    ...(copies.cc.length ? { cc: copies.cc } : {}),
                    ...(copies.bcc.length ? { bcc: copies.bcc } : {}),
                    ...(finalHtml ? { html: finalHtml } : {}),
                });
            }
            setBody('');
            setEmailHtml('');
            setEmailCc('');
            setEmailBcc('');
            setMediaUrl('');
            setAttachmentError('');
            onTemplateSelect?.('');
            setNotice(sendResult?.pending ? 'Delivery is being checked. Do not resend this message.' : `Sent to ${formatPhone(address) || address} on ${describeChannel(selectedChannel)}.`);
            if (typeof onSent === 'function') onSent(entry);
        } catch (sendError) {
            setError(sendError.message || 'The reply could not be sent.');
            if (sendError.outcome === 'unconfirmed') {
                // The text may still have been sent. The thread shows it with an amber "check the imported provider" note;
                // clearing the draft means a resend is a deliberate retype, never one click on Send.
                setBody('');
                setMediaUrl('');
                setAttachmentError('');
            }
        } finally {
            sendInFlightRef.current = false;
            setSending(false);
        }
    };

    // BL-177 Step 3: "Schedule send…". The same checks as Send; the scheduler signs each eligible message when it is sent.
    const handleSchedule = async ({ at, cancelOnReply }) => {
        setScheduleError('');
        setError('');
        setNotice('');
        if (!selectedChannel) { setScheduleError('Select a message type before scheduling.'); return false; }
        if (isEmail && (emailCc.trim() || emailBcc.trim() || emailHtmlHasFormatting(emailHtml))) {
            setScheduleError('Scheduled emails go out as plain text to the client only. Remove the Cc / Bcc and formatting, or send now.');
            return false;
        }
        if (signatureContext.loading || signatureContext.error) {
            setScheduleError(signatureContext.error || 'Signature settings are still loading. Please try again.');
            return false;
        }
        const validation = validateStaffReply({ session: intakeSession, body: bodyToSend, channel: selectedChannel, contentSid: selectedTemplateSid, mediaUrl });
        if (!validation.valid) { setScheduleError(validation.error); return false; }
        if (!canReply) { setScheduleError('Another staff member is engaged in this conversation. Request to join before replying.'); return false; }
        if (smsBlocked) { setScheduleError(smsOptOutMessage(contact?.sms_opted_out_at)); return false; }
        if (selectedTemplateSid && (!selectedTemplate || templateIncomplete)) { setScheduleError('Fill in every required template field before scheduling.'); return false; }
        if (templateScopeMismatch) { setScheduleError('Choose a template that belongs to the selected sending number account.'); return false; }
        setScheduling(true);
        try {
            const deliveryChannel = selectedChannel === 'sms' && mediaUrl.trim() ? 'mms' : selectedChannel;
            const contentSid = selectedChannel === 'whatsapp'
                ? (selectedTemplate?.contentSid || selectedTemplateSid)
                : '';
            // Comms gate fixes: the time-of-day greeting suits the moment the message is delivered, not when it was written.
            const deliveryValues = selectedTemplate ? valuesForDeliveryTime(selectedTemplate, templateVariables, { at }) : templateVariables;
            const deliveryBody = selectedTemplate
                ? fillTemplateBody(selectedTemplate.body || selectedTemplate.types?.['twilio/text']?.body, deliveryValues)
                : bodyToSend;
            const scheduled = await scheduleStaffReply({
                session: intakeSession,
                body: deliveryBody,
                channel: deliveryChannel,
                subject: selectedChannel === 'email' ? subject : '',
                mediaUrl: mediaUrl.trim() || null,
                contentSid,
                contentVariables: contentSid ? deliveryValues : null,
                templateBody: contentSid ? (selectedTemplate?.body || selectedTemplate?.types?.['twilio/text']?.body || '') : '',
                sendingIdentityId: selectedIdentity?.sourceId || null,
                sendingIdentityExplicitlyChosen: Boolean(selectedIdentityId),
                scheduledFor: at,
                cancelOnReply,
                client,
            });
            setBody('');
            setEmailHtml('');
            setMediaUrl('');
            setAttachmentError('');
            onTemplateSelect?.('');
            setNotice(`Scheduled for ${formatScheduledWhen(scheduled?.scheduled_for || at, scheduledTimeZone)}.`);
            onScheduled?.(scheduled);
            return true;
        } catch (scheduleFailure) {
            setScheduleError(scheduleFailure.message || 'The message could not be scheduled.');
            return false;
        } finally {
            setScheduling(false);
        }
    };

    const switchChannel = (nextChannel) => {
        // Into Email: the typed text becomes the formatted body. Out of Email: the plain-text twin (`body`) is what stays.
        if (nextChannel === 'email' && selectedChannel !== 'email') setEmailHtml(plainTextToEmailHtml(body));
        if (nextChannel !== 'email' && selectedChannel === 'email') setEmailHtml('');
        setPendingChannel('');
        rememberReplyChannel(intakeSession, nextChannel, currentUserId || '');
        setAutoChannelReason('');
        setSelectedChannel(nextChannel);
        setError('');
        setNotice('');
        setSelectedIdentityId('');
        onTemplateSelect?.('');
    };
    const handleChannelChange = (nextChannel) => {
        if (selectedChannel === 'email' && nextChannel !== 'email' && emailHtmlHasFormatting(emailHtml)) {
            setPendingChannel(nextChannel);
            return;
        }
        switchChannel(nextChannel);
    };
    const handleEmailHtmlChange = (html) => {
        setEmailHtml(html);
        handleBodyChange({ target: { value: emailHtmlToText(html) } });
    };

    const handleBodyChange = (event) => {
        const nextBody = event.target.value;
        setBody(nextBody);
        if (!engagementStartedRef.current && nextBody.trim() && canReply) {
            engagementStartedRef.current = true;
            if (typeof onEngagementStart === 'function') onEngagementStart();
        }
    };

    const uploadAttachment = async (event) => {
        const file = event.target.files?.[0];
        event.target.value = '';
        setAttachmentError('');
        if (!file) return;
        if (!['image/jpeg', 'image/png', 'image/gif', 'application/pdf'].includes(file.type) || file.size > 10 * 1024 * 1024) {
            setAttachmentError('Choose a JPG, PNG, GIF, or PDF file up to 10 MB.');
            return;
        }
        try {
            const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
            const objectPath = `${intakeSession.org_id}/${sessionId}/${crypto.randomUUID()}-${safeName}`;
            const { error: uploadError } = await client.storage.from(OUTBOUND_MEDIA_BUCKET).upload(objectPath, file, {
                contentType: file.type,
                upsert: false,
            });
            if (uploadError) throw uploadError;
            const { data, error: urlError } = await client.storage.from(OUTBOUND_MEDIA_BUCKET).createSignedUrl(objectPath, 3600);
            if (urlError || !data?.signedUrl) throw urlError || new Error('Could not prepare the attachment.');
            setMediaUrl(data.signedUrl);
            setNotice(`Attached ${file.name}`);
        } catch (uploadError) {
            setAttachmentError(uploadError?.message || 'Could not upload the attachment.');
        }
    };


    // BL-177 Step 2c (F3): short codes, sender names and group texts can be read here but not answered; say why.
    const noReplyReason = replyBlockedReason(intakeSession);
    if (noReplyReason) return <p role="note" className="p-3 text-sm text-white">{noReplyReason}</p>;

    return (
        <form ref={formRef} onSubmit={handleSend} className="min-w-0">
            <DeliveryReview client={client} sessionId={sessionId} refreshKey={notice} onResolved={()=>{composeActionRef.current=null;setNotice('Delivery review saved. Check the conversation before starting a new message.');}} />
            {canCompose && (
                <MessageComposerBox
                    theme={theme}
                    composeKey={sessionId || 'reply'}
                    body={body}
                    onBodyChange={(nextBody) => {
                        handleBodyChange({ target: { value: nextBody } });
                    }}
                    bodyPlaceholder={`Reply to ${firstNameOnly(contact?.name || intakeSession?.contact_name, 'customer')}…`}
                    bodyAriaLabel="Reply message"
                    channelNotice={channelNotice}
                    selectedChannel={selectedChannel}
                    channelOptions={channelOptions.map((option) => {
                        const usable = channelOptions.includes(option);
                        const optedOut = option === 'sms' && smsOptedOut;
                        const identities = sendingIdentities.filter((identity) => identity.channel === option);
                        const blockedReason = identities.length > 0 && identities.every((identity) => identity.blockedReason)
                            ? identities[0].blockedReason
                            : '';
                        const requiresWhatsAppTemplate = option === 'whatsapp' && !withinWhatsAppWindow;
                        // 1.12.212-beta: a landline can't take a text; a number that bounced WhatsApp (63003) isn't on it.
                        const lineBlock = option === 'sms' && lineSms.state === 'blocked' ? lineSms.reason : '';
                        const whatsappBlock = option === 'whatsapp' && lineWhatsApp.state === 'not_on' ? lineWhatsApp.reason : '';
                        return {
                            value: option,
                            label: describeChannel(option),
                            disabled: !usable || optedOut || Boolean(blockedReason) || Boolean(lineBlock) || Boolean(whatsappBlock),
                            reason: blockedReason || (optedOut ? 'This person opted out of texts.' : '') || lineBlock || whatsappBlock
                                || (option === 'sms' && lineSms.state === 'warn' ? lineSms.reason : '')
                                || (requiresWhatsAppTemplate ? 'Choose an approved WhatsApp template outside the 24-hour window.' : ''),
                        };
                    })}
                    onChannelChange={handleChannelChange}
                    selectedIdentity={selectedIdentity}
                    channelIdentities={channelIdentities}
                    selectedIdentityId={selectedIdentityId}
                    onIdentityChange={setSelectedIdentityId}
                    selectedTemplate={selectedTemplate}
                    selectedTemplateSid={selectedTemplateSid}
                    templateVariables={templateVariables}
                    onTemplateVariableChange={(key, value) => onTemplateSelect?.(selectedTemplateSid, { ...templateVariables, [key]: value })}
                    templateIncomplete={templateIncomplete}
                    templates={templates}
                    whatsappTemplateAccountSid={whatsappTemplateAccountSid}
                    onTemplateChoose={(template) => onTemplateSelect?.(
                        template.sid,
                        buildTemplateVariableValues(template, { contact, trip: intakeSession, session: intakeSession, companyName: templateCompanyName }, { useSampleFallback: template.channel !== 'sms' })
                    )}
                    onTemplateRemove={() => onTemplateSelect?.('')}
                    onTemplatePanelRequestOpen={onTemplatePanelRequestOpen}
                    onEditTemplates={openTemplateSettings}
                    canEditTemplates={canEditTemplates}
                    onUploadAttachment={uploadAttachment}
                    onInsertText={(kind) => setBody((current) => `${current}${current ? '\n' : ''}${kind === 'location' ? (intakeSession?.pickup_address || intakeSession?.pickup_location || '') : (intakeSession?.itinerary_short_form || '')}`)}
                    subject={subject}
                    onSubjectChange={setSubject}
                    email={isEmail ? {
                        actions: mailboxActions,
                        to: address,
                        cc: emailCc,
                        bcc: emailBcc,
                        onCcChange: setEmailCc,
                        onBccChange: setEmailBcc,
                        ccError,
                        bccError,
                        html: emailHtml,
                        onHtmlChange: handleEmailHtmlChange,
                    } : null}
                    showSubject={selectedChannel === 'email'}
                    subjectAriaLabel="Email subject"
                    subjectPlaceholder="Subject"
                    sending={sending}
                    disabled={!canReply}
                    sendDisabled={
                        (!trimmedLength && !mediaUrl.trim() && !selectedTemplateSid)
                        || !selectedChannel
                        || overLimit
                        || !canReply
                        || smsBlocked
                        || signatureContext.loading
                        || Boolean(signatureContext.error)
                        || (whatsappTemplateRequired && !selectedTemplateSid)
                        || (selectedTemplateSid && templateIncomplete)
                        || templateScopeMismatch
                        || !address
                        || Boolean(copyError)
                    }
                    sendDisabledReason={
                        // BL-177 Step 2d: say why Send is off when it isn't simply an empty message.
                        signatureContext.error || (signatureContext.loading ? 'Loading signature settings…' : '')
                            || (!canReply || smsBlocked ? ''
                            : !selectedChannel ? 'Select a message type to reply.'
                                : !address ? ''
                                : whatsappTemplateRequired && !selectedTemplateSid ? 'Choose an approved WhatsApp template to send.'
                                    : selectedTemplateSid && templateIncomplete ? 'Fill in every template value to send.'
                                        : overLimit ? 'This message is over the length limit.'
                                            : copyError || '')
                    }
                    onSend={() => handleSend({ preventDefault() {} })}
                    counterSignature={signatureApplies ? signature : null}
                    signatureChip={signatureChipFor(signature, {
                        removeMine,
                        onToggle: () => setRemoveMine((current) => !current),
                        available: !signatureContext.loading && !signatureContext.error,
                    })}
                    helpAriaLabel="WhatsApp reply help"
                    userId={currentUserId}
                    scheduleSendEnabled={scheduleSendEnabled}
                    onScheduleSend={onScheduleSend}
                    schedule={scheduleSendEnabled ? {
                        enabled: true, companyTimeZone: scheduledTimeZone, recipientTimeZone: recipientZone,
                        busy: scheduling, error: scheduleError, onConfirm: handleSchedule,
                    } : null}
                />
            )}
            {pendingChannel && (
                <div className="mt-1 flex flex-wrap items-center gap-2 rounded-xl border border-[#FDBA74] px-3 py-2 text-xs text-[var(--ink)]" role="alertdialog" aria-label="Formatting will be lost" data-format-loss-warning="true">
                    <span className="min-w-0 flex-1">{describeChannel(pendingChannel)} is plain text, so the bold, lists and links in this email will be removed. The words stay.</span>
                    <button type="button" className="ia-btn3d ia-comms-pill ia-btn3d--go" onClick={() => switchChannel(pendingChannel)}>Switch to {describeChannel(pendingChannel)}</button>
                    <button type="button" className="ia-btn3d ia-comms-pill" onClick={() => setPendingChannel('')}>Keep email</button>
                </div>
            )}
            {attachmentError && <p className="mt-1 text-xs text-[#FCA5A5]" role="alert">{attachmentError}</p>}
            {!canReply && <p className="mt-1 text-xs text-[#FDBA74]">Another staff member is engaged in this conversation. Request to join before replying.</p>}
            {smsBlocked && <p className="mt-1 text-xs text-[#FCA5A5]">{smsOptOutMessage(contact?.sms_opted_out_at)}</p>}
            {signatureContext.error && <p className="mt-1 text-xs text-[#FCA5A5]" role="alert">{signatureContext.error}</p>}
            {selectedTemplateSid && templateScopeMismatch && <p className="mt-1 text-xs text-[#FCA5A5]" role="alert">Choose a template that belongs to the selected sending number account.</p>}
            {error && <p className={`text-xs ${error === UNCONFIRMED_SEND_MESSAGE ? 'text-[#FDBA74]' : 'text-[#FCA5A5]'}`} data-tone={error === UNCONFIRMED_SEND_MESSAGE ? 'orange' : 'red'} role="alert">{error}</p>}
            {notice && <p className="text-xs text-[#86EFAC]">{notice}</p>}
        </form>
    );
}

// App chat stays inside the existing inbox composer and uses only its caller-JWT RPC.
function StaffReplyComposer(props) {
    return props.intakeSession?.channel === 'app_chat'
        ? <AppChatReplyComposer {...props} />
        : <TransportStaffReplyComposer {...props} />;
}

export default StaffReplyComposer;
